import { useEffect, useRef } from 'react';

import { PRESET_CUE_MESSAGE_PARAM } from 'utils/constants';
import { removeParamsFromUrlWithReplace } from 'utils/urlUtils';

const TOGGLE_RETRY_MS = 500;
const MAX_WAIT_MS = 30000;
const LOG_PREFIX = '[usePresetCueMessage]';

// LocalStorage key substrings used by the WxCopilot bundle to persist
// thread state (see CopilotProductV2/context.tsx getStorageKey /
// getAgentNetStorageKey). Clearing any matching key forces the bundle to
// create a fresh thread on next load.
const COPILOT_STORAGE_KEY_MARKERS = ['-copilot-thread-', '-copilot-agent-net-'];

const HIRE_STORAGE_KEY_PREFIX = 'hire-';

/**
 * Matches cookieStorage.getCookieDomain() in the WxCopilot bundle: on
 * localhost returns 'localhost'; in production returns '.<apex>' so the
 * cross-domain cookie covers employer.* and web.* subdomains.
 */
function getCookieDomain() {
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
  const parts = hostname.split('.');
  return `.${parts.slice(-2).join('.')}`;
}

/**
 * Clears any persisted copilot thread state so the bundle creates a fresh
 * thread. Returns the list of keys cleared for logging.
 */
function clearCopilotThreadStorage() {
  const cleared = [];
  const toRemove = [];

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && COPILOT_STORAGE_KEY_MARKERS.some(m => key.includes(m))) {
      toRemove.push(key);
    }
  }

  toRemove.forEach(key => {
    localStorage.removeItem(key);
    cleared.push(key);

    // Hire product also stores the thread uuid in a cross-domain cookie
    // (cookieStorage.setCookieThreadUuid). Expire it so the bundle can't
    // fall back to a cookie-stored thread uuid.
    if (key.startsWith(HIRE_STORAGE_KEY_PREFIX)) {
      try {
        const domain = getCookieDomain();
        document.cookie = `${key}=; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`${LOG_PREFIX} failed to clear cookie ${key}`, err);
      }
    }
  });

  return cleared;
}

/**
 * Reads the `preset_cue_message` URL parameter, forces a fresh Cue thread
 * by clearing persisted thread state, opens the copilot panel (retrying
 * because `toggleCopilotPanel` no-ops while the bundle's internal
 * `copilot`/`thread`/`employerUuid` are still loading), sends the message,
 * then strips the URL param.
 *
 * Readiness signal: the bundle emits a `wx-copilot-panel` CustomEvent with
 * `isOpen: true` once the panel actually opens. We use that — not the
 * earlier `wx-copilot-button` event — as the confirmation that toggle
 * succeeded.
 *
 * Payload shape is `{ type: 'user', data: string }` — SendMessageInput in
 * CopilotAgentStreamV3/types.ts.
 *
 * @param {string} mountId - The WxCopilot mount instance ID
 */
export default function usePresetCueMessage(mountId) {
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return undefined;
    if (!mountId) return undefined;

    const urlParams = new URLSearchParams(window.location.search);
    const presetMessage = urlParams.get(PRESET_CUE_MESSAGE_PARAM);

    if (!presetMessage) {
      // eslint-disable-next-line no-console
      console.debug(
        `${LOG_PREFIX} no ${PRESET_CUE_MESSAGE_PARAM} param; skipping`,
      );
      return undefined;
    }

    // eslint-disable-next-line no-console
    console.info(
      `${LOG_PREFIX} found preset message; forcing new thread and waiting for panel`,
      { presetMessage, mountId },
    );

    // Force a fresh thread. Do this synchronously before the bundle has a
    // chance to read the persisted thread uuid from localStorage/cookies.
    const clearedKeys = clearCopilotThreadStorage();
    // eslint-disable-next-line no-console
    console.info(
      `${LOG_PREFIX} cleared ${clearedKeys.length} copilot thread storage key(s)`,
      clearedKeys,
    );

    let retryTimeoutId = null;
    let maxWaitTimeoutId = null;

    const tryToggle = () => {
      if (sentRef.current) return;

      const instance = window.WxCopilotComponentInstances?.[mountId];
      if (!instance?.toggleCopilotPanel) {
        // eslint-disable-next-line no-console
        console.debug(
          `${LOG_PREFIX} instance.toggleCopilotPanel not available yet; retrying`,
        );
      } else {
        // eslint-disable-next-line no-console
        console.debug(
          `${LOG_PREFIX} attempting toggleCopilotPanel (will retry if it no-ops)`,
        );
        try {
          instance.toggleCopilotPanel();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`${LOG_PREFIX} toggleCopilotPanel threw`, err);
        }
      }

      retryTimeoutId = setTimeout(tryToggle, TOGGLE_RETRY_MS);
    };

    const onPanelEvent = ev => {
      const detail = ev?.detail;
      // eslint-disable-next-line no-console
      console.debug(`${LOG_PREFIX} wx-copilot-panel event`, detail);
      if (sentRef.current) return;
      if (!detail?.isOpen) return;

      // Panel actually opened — stop retrying and send the message.
      sentRef.current = true;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }
      if (maxWaitTimeoutId) {
        clearTimeout(maxWaitTimeoutId);
        maxWaitTimeoutId = null;
      }

      const instance = window.WxCopilotComponentInstances?.[mountId];
      if (!instance?.sendMessageToQueue) {
        // eslint-disable-next-line no-console
        console.error(
          `${LOG_PREFIX} panel opened but sendMessageToQueue missing`,
          { instance },
        );
        return;
      }

      const payload = { type: 'user', data: presetMessage };
      try {
        // eslint-disable-next-line no-console
        console.info(
          `${LOG_PREFIX} panel confirmed open; sending message to queue`,
          payload,
        );
        instance.sendMessageToQueue(payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`${LOG_PREFIX} sendMessageToQueue threw`, err);
      }

      removeParamsFromUrlWithReplace({ [PRESET_CUE_MESSAGE_PARAM]: null });
    };

    window.addEventListener('wx-copilot-panel', onPanelEvent);

    // Start retrying immediately. The first attempt is almost always a
    // no-op (bundle still loading), but subsequent retries will succeed as
    // soon as productConfig/copilot/thread/employerUuid are all populated.
    tryToggle();

    maxWaitTimeoutId = setTimeout(() => {
      if (!sentRef.current) {
        // eslint-disable-next-line no-console
        console.warn(
          `${LOG_PREFIX} gave up after ${MAX_WAIT_MS}ms — panel never opened`,
        );
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          retryTimeoutId = null;
        }
      }
    }, MAX_WAIT_MS);

    return () => {
      window.removeEventListener('wx-copilot-panel', onPanelEvent);
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      if (maxWaitTimeoutId) clearTimeout(maxWaitTimeoutId);
    };
  }, [mountId]);
}
